import { AlertTriangleIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { Skeleton } from "@/components/ui/skeleton";
import { useTherapistSlots } from "@/hooks/admin-portal/scheduling/use-therapist-slots";
import { formatClock } from "@/lib/therapist-scheduling/schedule-board";
import { cn } from "@/lib/utils";

/**
 * The per-day detail the monthly cell no longer carries: which hours are still
 * open, which are taken, and — when the day is closed — why.
 *
 * Mounted only while a day is expanded, so the /slots request is made on demand
 * rather than for every day on the board.
 *
 * Design note: this panel is laid out as a labelled definition list at the same
 * density as the appointment table it sits above, rather than as a tinted box of
 * badges. It reads as another row of the same document instead of a popover that
 * wandered in.
 *
 * It is also where `booked_slots` finally makes sense. The cell can't carry that
 * number safely — "6/18" reads as a third full for a therapist with one visit —
 * because the sentence that makes it true doesn't fit in a table cell. Here it
 * does, so the ratio ships with its own explanation attached.
 *
 * Note on what is NOT here: upstream's `infeasible_slots` (slots unreachable on
 * the road) needs customer coordinates to compute, and this board has no
 * customer — so `include_infeasible` would be a no-op and is not sent.
 */

/** One labelled row: label in a fixed gutter, value free to wrap beside it. */
function DetailRow({
	label,
	children,
}: {
	label: string;
	children: ReactNode;
}) {
	return (
		<div className="grid grid-cols-[minmax(0,5.5rem)_minmax(0,1fr)] gap-x-3 gap-y-1 items-baseline">
			<dt className="text-[10px] uppercase tracking-widest text-muted-foreground/80">
				{label}
			</dt>
			<dd className="min-w-0 text-xs">{children}</dd>
		</div>
	);
}

/**
 * Times as square hairline chips — a gauge of the day, not decoration.
 *
 * Start times only. These are candidate *start* times, and the span is constant
 * per therapist (duration + buffer from `schedule_config`), so printing the end
 * on every chip repeats the same fact N times and halves how many fit on a line.
 * The length of a visit belongs on the therapist, not on each slot.
 */
function TimeChips({
	slots,
	taken,
}: {
	slots: { start_time: string }[];
	taken?: boolean;
}) {
	const { locale } = useDateContext();

	return (
		<div className="flex flex-wrap gap-1">
			{slots.map((slot) => (
				<span
					key={`${taken ? "taken" : "open"}-${slot.start_time}`}
					className={cn(
						"px-1.5 py-0.5 border text-[11px] tabular-nums tracking-tight",
						taken
							? "border-transparent bg-muted text-muted-foreground line-through decoration-muted-foreground/40"
							: "border-border bg-background",
					)}
				>
					{formatClock(slot.start_time, locale.code)}
				</span>
			))}
		</div>
	);
}

export default function DayAvailabilityPanel({
	therapistId,
	date,
}: {
	therapistId: string;
	date: string;
}) {
	const { t } = useTranslation("therapist-schedules");
	// bypass so an out-of-contract day explains itself instead of coming back bare
	const { data, isLoading, error } = useTherapistSlots(therapistId, date, {
		bypassContractCheck: true,
	});

	if (isLoading) {
		return (
			<div className="px-3 py-2.5 border-t border-border/60">
				<Skeleton className="w-2/3 h-4" />
			</div>
		);
	}

	if (error) {
		return (
			<p className="px-3 py-2.5 text-xs italic border-t text-muted-foreground border-border/60">
				{t("day.availability.unavailable_service")}
			</p>
		);
	}

	const slots = data?.data;
	if (!slots) return null;

	const available = slots.available_slots ?? [];
	const booked = slots.booked_slots ?? [];
	const notices = [
		slots.contract_warning &&
			t(`day.availability.contract.${slots.contract_warning.status}`, {
				start: slots.contract_warning.contract_start_date ?? "-",
				end: slots.contract_warning.contract_end_date ?? "-",
			}),
		slots.unavailable_reason,
	].filter(Boolean) as string[];

	return (
		<dl className="px-3 py-2.5 space-y-2 border-t border-border/60 bg-muted/20">
			{notices.map((notice) => (
				<p
					key={notice}
					className="flex items-start gap-1.5 pl-2 text-xs font-medium border-l-2 border-destructive text-destructive"
				>
					<AlertTriangleIcon className="h-3 w-3 mt-0.5 shrink-0" />
					<span className="text-pretty">{notice}</span>
				</p>
			))}

			<DetailRow label={t("day.availability.open_hours")}>
				{available.length === 0 ? (
					<span className="italic text-muted-foreground">
						{t("day.availability.none")}
					</span>
				) : (
					<TimeChips slots={available} />
				)}
			</DetailRow>

			{booked.length > 0 && (
				<DetailRow label={t("day.availability.taken_hours")}>
					<TimeChips slots={booked} taken />
				</DetailRow>
			)}

			{/* the one place the candidate-slot ratio can be stated without misleading:
			    there is room here for the sentence that makes it true */}
			<DetailRow label={t("day.availability.candidates")}>
				<span className="tabular-nums">
					{t("day.availability.candidates_value", {
						booked: slots.total_booked_slots,
						total: slots.total_available_slots + slots.total_booked_slots,
					})}
				</span>
				<span className="block mt-0.5 text-[11px] leading-snug text-muted-foreground text-pretty">
					{t("day.availability.candidates_hint")}
				</span>
			</DetailRow>
		</dl>
	);
}
